/**
 * OPENING A STRIPE CHECKOUT WITHOUT LOSING THE PAGE THE CUSTOMER WAS ON (2026-10-14).
 *
 * The user, twice: *"the stripe payment screen is still not showing for them to pay"* and *"The booking cannot be
 * continued as the page they were on was lost, meaning they had to go and start booking all over again."*
 *
 * TWO SEPARATE FAULTS, ONE CAUSE.
 *
 * 1. `window.open(url)` AFTER an `await` is not treated as user-initiated, so the browser blocks it. The fallback
 *    was `window.location.href = url` — which navigates the CURRENT tab to Stripe and destroys everything the
 *    customer had built on the page (their service, date, time, package). That is the lost page.
 *
 * 2. So the tab is RESERVED BEFORE the request: `window.open("", "_blank")` inside the click handler IS
 *    user-initiated and is never blocked, and its location is set once the server answers. No `await` sits between
 *    the gesture and the window.
 *
 * AND IF IT IS STILL BLOCKED — a popup blocker that refuses even the reserved window, or a browser that returns
 * `null` — we do NOT navigate away. Navigating is what lost the page, and losing the page is the worse failure.
 * The URL is offered as a link instead, so the customer chooses.
 *
 * `noopener` is deliberately NOT passed here: it makes some browsers return `null` even on success, which is
 * indistinguishable from a block and would send us down the fallback path for no reason. The window is then
 * closed on `window.opener` by hand so the opened page cannot reach back into this one.
 */

/** Reserve a tab NOW (inside the click), fill it in later. Returns null when a popup blocker refused. */
function reservePaymentTab() {
  if (typeof window === "undefined") return null;
  const tab = window.open("", "_blank");
  if (!tab) return null;
  // The reserved window is blank and same-origin for a moment; write something so it is not a dead tab if the
  // request is slow, and in case the close-on-opener below cannot run.
  try {
    tab.document.write(
      "<p style=\"font-family:system-ui;padding:24px;color:#374151\">Opening secure payment…</p>",
    );
  } catch {
    // A cross-origin or otherwise restricted window: nothing to do, it will be navigated shortly.
  }
  return tab;
}

/**
 * Send a reserved tab to its URL.
 *
 * @returns {boolean} true when the payment is on its way in another tab.
 */
function sendReservedTabToPayment(tab, url) {
  if (!tab || tab.closed) return false;
  try {
    if (tab.opener) tab.opener = null;
    tab.location.replace(url);
    tab.focus();
    return true;
  } catch {
    return false;
  }
}

export { reservePaymentTab, sendReservedTabToPayment };
